import { CommonModule, NgOptimizedImage } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgClickOutsideDirective } from 'ng-click-outside2';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { UiSwitchModule } from 'ngx-ui-switch';
import { NgxSliderModule } from '@angular-slider/ngx-slider';

/* ----------- * PIPES * ----------- */
import { PipeDropdownSearch } from '../shared/components/mtm-dropdown/common/dropdown-search.pipe';
import { MtmDropdownComponent } from '../shared/components/mtm-dropdown/mtm-dropdown.component';
import { MTMTelInputPipe } from '../shared/components/mtm-tel-input/pipe/mtm-tel-input.pipe';
import { ArraySortPipe } from '../shared/pipes/array-sort.pipe';
import { CleanUrl } from '../shared/pipes/clean-link.pipe';
import { CleanerPipe } from '../shared/pipes/cleaner.pipe';
import { FilterSearchProjectPipe } from '../shared/pipes/filter-search-projects.pipe';
import { FilterSearchUserPipe } from '../shared/pipes/filter-search-user.pipe';
import { ProjectSortingPipe } from '../shared/pipes/filter-sorting-project.pipe';
import { KeysPipe } from '../shared/pipes/keys.pipe';
import { MtmDatePipe } from '../shared/pipes/mtm-date.pipe';
import { MtmDatePipe2 } from '../shared/pipes/mtm-date.pipe2';
import { ToTimePipe } from '../shared/pipes/number-to-time.pipe';
import { ObjectToKeysPipe } from '../shared/pipes/object-to-keys.pipe';
import { OrdinalPipe } from '../shared/pipes/ordinal.pipe';
import { ParseUrl } from '../shared/pipes/parse-url.pipe';
import { SafePipe } from '../shared/pipes/safe.pipe';
import { SearchCompanyPipe } from '../shared/pipes/search-company.pipe';
import { FilterPipe } from '../shared/pipes/search-file.pipe';
import { SearchUserPipe } from '../shared/pipes/search-user.filter';
import { SortCompanyPipe } from '../shared/pipes/sort-company.pipe';

/* ----------- * DIRECTIVES * ----------- */
import { CommentMinButtonsDirective } from '../shared/directives/comment-min-buttons.directive';
import { AssetPreviewDirective } from '../shared/directives/asset-preview.directive';
import { CommentTextAreaDirective } from '../shared/directives/comment-textarea.directive';
import { GroupAvatarDirective } from '../shared/directives/group-avatar.directive';
import { MTMDatePickerDirective } from '../shared/directives/mtm-datepicker.directive';
import { SSProfilePreviewImageDirective } from '../shared/directives/ss-profile-preview-image.directive';
import { HasUserAccessDirective } from '../shared/directives/user-access.directive';
import { UserAvatarDirective } from '../shared/directives/user-avatar.directive';
import { ClickOutsideDirective } from '../shared/directives/click-outside.directive';

/* ----------- * PAGES * ----------- */
import { ProjectCompanyComponent } from '../layouts/my-projects/new-project/project-company/project-company.component';

/* ----------- * COMPONENTS * ----------- */
import { NotificationViewComponent } from '../layouts/user-notifications/notification-view/notification-view.component';
import { CircleProgressComponent } from '../on-boarding/profile-avatar/circle-progress/circle-progress.component';
import { ButtonComponent } from '../shared/components/button/button.component';
import { EmbedVideoComponent } from '../shared/components/embed-video/embed-video.component';
import { FilterDropdownComponent } from '../shared/components/filter-dropdown/filter-dropdown.component';
import { LoadingDataComponent } from '../shared/components/loading-data/loading-data.component';
import { MTMMainInfiniteScrol } from '../shared/components/mtm-main-infinite-scroll/mtm-main-infinite-scroll.component';
import { MTMTelInputComponent } from '../shared/components/mtm-tel-input/mtm-tel-input.component';
import { UploadFileComponent } from '../shared/components/mtm-upload-file/upload-file.component';
import { NewUploadFilesPreviewThumpnailComponent } from '../shared/components/newupload-files-preview-thumpnail/newupload-files-preview-thumpnail.component';
import { UserCardComponent } from '../shared/components/user-card/user-card.component';
import { MTMTermConditionCheckComponent } from '../terms-conditions/terms-condition-check/terms-condition-check.component';

/* ----------- * MODAL_COMPONENTS * ----------- */
import { UploadModalComponent } from '../on-boarding/profile-avatar/upload-modal/upload-modal.component';
import { CommentAnnotation } from '../shared/components/comment-annotation/comment-annotation.component';
import { CommentPopupComponent } from '../shared/components/comment-popup/comment-popup.component';
import { CommentDatePipe } from '../shared/pipes/comment-date-pipe.pipe';
import { CompanyRolesAndPermissionComponent } from '../shared/components/company-roles-and-permissions/company-roles-and-permissions.component';
import {
  HierarchicalFilesComponent,
  HRBadgebtn,
  HRGridFile,
  HRListFile,
  HRPreviewFile,
} from '../shared/components/hierarchical-files';
import { ImageAnnotation } from '../shared/components/image-annotation/image-annotation.component';
import { ImageCropperModule } from '../shared/components/image-cropper/image-cropper.module';
import { ImageDrawingTools } from '../shared/components/media-player/components/image-drawing-tools/image-drawing-tools.component';
import { MtmPdfSliderComponent } from '../project-subsection/preview/mtm-pdf-slider/mtm-pdf-slider.component';
import { VideojsAnnotationBarListComponent } from '../shared/components/media-player/components/videojs-annotation-bar-list/videojs-annotation-bar-list.component';
import { MTMDownloadUploadProgressIcon } from '../shared/components/mtm-download-upload-progress-icon/mtm-download-upload-progress-icon.component';
import { LoadingSpinnerComponent } from '../shared/components/loading-spinner/loading-spinner.component';
import { StatusSelectComponent } from '../shared/components/status-select/status-select.component';
import { MTMFileDownloadControl } from '../shared/components/mtm-file-download/mtm-file-download-control/mtm-file-download-control.component';
import { MtmItemIconComponent } from '../shared/components/mtm-item-icon/mtm-item-icon.component';
import { MtmChatSparkleIconComponent } from '../shared/components/mtm-chat-sparkle-icon/mtm-chat-sparkle-icon.component';
import { MtmTaskIconComponent } from '../shared/components/mtm-task-icon/mtm-task-icon.component';
import { MtmResizeIndicatorIconComponent } from '../shared/components/mtm-resize-indicator-icon/mtm-resize-indicator-icon.component';
import { MtmReadAllIconComponent } from '../shared/components/mtm-read-all-icon/mtm-read-all-icon.component';
import { MtmNotificationSettingsIconComponent } from '../shared/components/mtm-notification-settings-icon/mtm-notification-settings-icon.component';
import { MtmGlobeIconComponent } from '../shared/components/mtm-globe-icon/mtm-globe-icon.component';
import { MtmReplyIconComponent } from '../shared/components/mtm-reply-icon/mtm-reply-icon.component';
import { MtmUnmuteIconComponent } from '../shared/components/mtm-unmute-icon/mtm-unmute-icon.component';
import { MtmArchiveIconComponent } from '../shared/components/mtm-archive-icon/mtm-archive-icon.component';
import { MtmCheckedIconComponent } from '../shared/components/mtm-checked-icon/mtm-checked-icon.component';
import { PdfAnnotation } from '../shared/components/pdf-annotation/pdf-annotation.component';
import { PreviewAllAssetComponent } from '../shared/components/preview-asset/preview-all-asset/preview-all-asset.component';
import { ProjectDescription } from '../shared/components/project-description/project-description.component';
import { ReviewLinkAssetsPreviewComponent } from '../shared/components/review-link/review-link-assets-preview/review-link-assets-preview.component';
import { ReviewUiLinkIdentityComponent } from '../shared/components/review-link/review-link-identity/review-link-identity.component';
import { ReviewLinkNotFoundComponent } from '../shared/components/review-link/review-link-not-found/review-link-not-found.component';
import { ReviewUiLinkPasswordComponent } from '../shared/components/review-link/review-link-password/review-link-password.component';
import { ReviewLinkRestrictedComponent } from '../shared/components/review-link/review-link-restricted/review-link-restricted.component';
import { MtmAssetsDropdownComponent } from '../shared/components/review-link/review-link-settings/mtm-assets-dropdown/mtm-assets-dropdown.component';
import { MtmInviterDropdownComponent } from '../shared/components/review-link/review-link-settings/mtm-inviter-dropdown/mtm-inviter-dropdown.component';
import { ReviewUiLinkSettingsComponent } from '../shared/components/review-link/review-link-settings/review-link-settings.component';
import { UploadFileModalComponent } from '../shared/components/upload-file-modal/upload-file-modal.component';
import { FileNameDisplayDirective } from '../shared/directives/filename-display.directive';
import { InViewDirective } from '../shared/directives/in-view.directive';
import { ActivityLogComponent } from '../layouts/activity-log/activity-log.component';
import { LoginFormComponent } from '../login/login-form/login-form.component';
import { Verify2faComponent } from '../login/verify-2fa/verify-2fa.component';
import { TranslatePipe } from '../shared/pipes/translate.pipe';
import { VideoAnnotationComponent } from '../shared/components/video-annotation/video-annotation.component';

import { PickerModule } from '@ctrl/ngx-emoji-mart';
import { FullCalendarModule } from '@fullcalendar/angular'; // must go before plugins
import { NgSelectModule } from '@ng-select/ng-select';
import { MtmAssetListComponent } from '../shared/components/asset-list/asset-list.component';
import { VariantStatusDropdownComponent } from '../shared/components/asset-list/components/asset-card/components/variant-status-dropdown/variant-status-dropdown.component';
import { AssetStatusDropdownComponent } from '../shared/components/asset-list/components/asset-card/components/asset-status-dropdown/asset-status-dropdown.component';
import { AssetCardComponent } from '../shared/components/asset-list/components/asset-card/asset-card.component';
import { VariantDropdownMenuComponent } from '../shared/components/asset-list/components/asset-card/components/variant-dropdown-menu/variant-dropdown-menu.component';
import { AssetDropdownMenuComponent } from '../shared/components/asset-list/components/asset-card/components/asset-dropdown-menu/asset-dropdown-menu.component';
import { MtmEditAssetModalComponent } from '../shared/components/asset-list/components/edit-asset-modal/edit-asset-modal.component';
import { MtmEditAssetTechnicalVariantsComponent } from '../shared/components/asset-list/components/edit-asset-modal/components/technical-variants-table/edit-asset-technical-variants.component';
import { MtmAssetNameComponent } from '../shared/components/asset-name/asset-name.component';
import { MtmAssetTypeSelectorComponent } from '../shared/components/asset-type-selector/asset-type-selector.component';
import { CommentPreviewComponent } from '../shared/components/comment-preview/comment-preview.component';
import { MtmEditableLabel } from '../shared/components/editable-label/editable-label.component';
import { PasswordStrengthComponent } from '../shared/components/password-strength-meter/password-strength-meter.component';
import { PreviewAssetListComponent } from '../shared/components/preview-asset-list/preview-asset-list.component';
import { PreviewAssetComponent } from '../shared/components/preview-asset/preview-asset.component';
import { CreateTaskComponent } from '../shared/components/task/create-task/create-task.component';
import { PreviewTaskComponent } from '../shared/components/task/preview-task/preview-task.component';
import { SingleTaskComponent } from '../shared/components/task/single-task/single-task.component';
import { TaskAssetPreviewComponent } from '../shared/components/task/task-assets-preview/task-assets-preview.component';
import { TaskDropdownComponent } from '../shared/components/task/task-dropdown/task-dropdown.component';
import { TaskListComponent } from '../shared/components/task/task-list/task-list.component';
import { UrlShortenerEditorComponent } from '../shared/components/url-shortener/editor/url-shortener-editor.component';
import { UrlShortenerComponent } from '../shared/components/url-shortener/url-shortener.component';
import { NumberDirective } from '../shared/directives/number-only.directive';
import { PhoneNumberDirective } from '../shared/directives/phone-number-only.directive';
import { archivedProjectFilterPipe } from '../shared/pipes/archived-project-filter.pipe';
import { MtmDateTimePipe } from '../shared/pipes/mtm-date-time.pipe';
import { MtmFileSizePipe } from '../shared/pipes/mtm-file-size.pipe';
import { MtmNgbDate } from '../shared/pipes/mtm-ngb-date-format.pipe';
import { MtmTimePipe } from '../shared/pipes/mtm-time.pipe';
import { projectFilterPipe } from '../shared/pipes/project-filter.pipe';
import { ToUtcDate } from '../shared/pipes/to-utc.pipe';
import { FilterUser } from '../shared/pipes/user.filter';
import { GridSkeletonComponent } from '../shared/skeletons/grid-skeleton/grid-skeleton.component';
import { TimelineSkeletonComponent } from '../shared/skeletons/timeline-skeleton/timeline-skeleton.component';

import { QuillModule } from 'ngx-quill';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { GeneralLogComponent } from '../layouts/activity-log/general-log/general-log.component';
import { CalendarWidgetComponent } from '../layouts/my-projects/dashboard/calendar-widget/calendar-widget.component';
import { RecentActivityComponent } from '../layouts/my-projects/dashboard/recent-activity/recent-activity.component';
import { TaskWidgetComponent } from '../layouts/my-projects/dashboard/task-widget/task-widget.component';
import { DateCellRendererComponent } from '../shared/components/grid/renderer/date-cell/date-cell-renderer.component';
import { DateTimeCellRendererComponent } from '../shared/components/grid/renderer/date-time-cell/date-time-cell-renderer.component';
import { ImageAnnotationToolbarComponent } from '../shared/components/image-annotation-toolbar/image-annotation-toolbar.component';
import { KeyFileSelectorComponent } from '../shared/components/key-files/key-file-selector/key-file-selector.component';
import { MoveKeyFileComponent } from '../shared/components/key-files/move-key-file/move-key-file.component';
import { MtmAssetControlComponent } from '../shared/components/mtm-asset-control/mtm-asset-control.component';
import { MtmAssetTreeComponent } from '../shared/components/mtm-asset-control/mtm-project-asset-tree/mtm-asset-tree/mtm-asset-tree.component';
import { MtmProjectAssetTreeComponent } from '../shared/components/mtm-asset-control/mtm-project-asset-tree/mtm-project-asset-tree.component';
import { MtmImageComponent } from '../shared/components/mtm-image/mtm-image.component';
import { PasswordCheckerComponent } from '../shared/components/password-checker/password-checker.component';
import { ProjectsOwnershipTransferComponent } from '../shared/components/projects-ownership-transfer/projects-ownership-transfer.component';
import { TeamEditorComponent } from '../shared/components/team-editor/team-editor.component';
import { ObserveThisDirective } from '../shared/directives/observe-this.directive';
import { PasswordToggleDirective } from '../shared/directives/password-toggle.directive';
import { ResizeObserverElementDirective } from '../shared/directives/resize-observer-element.directive';
import { RightPaneContentDirective } from '../shared/directives/right-pane.directive';
import { ShortenedUrlDirective } from '../shared/directives/shortened-url.directive';
import { ThrottleClickDirective } from '../shared/directives/throttled-click.directive';
import { CacheBusterImagePipe } from '../shared/pipes/cache-buster-image.pipe';
import { FormatVersionNamePipe } from '../shared/pipes/format-version-name.pipe';
import { RoundedNumberPipe } from '../shared/pipes/limit-number.pipe';
import { MtmFileExtensionPipe } from '../shared/pipes/mtm-file-extension.pipe';
import { SimpleProjectFilterPipe } from '../shared/pipes/simple-project-filter.pipe';
import { InfoBannerComponent } from '../shared/components/info-banner/info-banner.component';
import { MtmTextEditorComponent } from './mtm-text-editor/mtm-text-editor.component';

import { OverlayModule } from '@angular/cdk/overlay';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MtmBreadcrumbComponent } from '../shared/components/mtm-breadcrumb/mtm-breadcrumb.component';
import { MtmBreadcrumbProjectComponent } from '../shared/components/mtm-breadcrumb-project/mtm-breadcrumb-project.component';
import { MtmBreadcrumbProjectCompareComponent } from '../shared/components/mtm-breadcrumb-project-compare/mtm-breadcrumb-project-compare.component';
import { ProjectStagesComponent } from '../layouts/my-projects/new-project/project-stages/project-stages.component';
import { DriveProjectStagesComponent } from '../layouts/my-projects/new-project/project-stages/drive-project-stages/drive-project-stages.component';
import { PdfTranscodeErrorComponent } from '../shared/components/media-player/components/pdf-transcode-error/pdf-transcode-error.component';
import { DisableShiftScrollDirective } from '../shared/directives/disable-shift-scroll.directive';
import { AssetsTableViewComponent } from '../shared/components/assets-table-view/assets-table-view.component';
import { RadialTransferBarComponent } from '../shared/components/filesupload/radial-transfer-bar/radial-transfer-bar.component';
import { AssetInfoComponent } from '../shared/components/media-player/components/asset-info/asset-info.component';
import { SubsectionPreviewAssetListComponent } from '../shared/components/media-player/components/asset-list/subsection-preview-asset-list.component';
import { AssetScanInfoComponent } from '../shared/components/media-player/components/asset-scan-info/asset-scan-info.component';
import { TopMediaInfoComponent } from '../shared/components/top-media-info/top-media-info.component';
import { TopMediaInfoCompareComponent } from '../shared/components/top-media-info-compare/top-media-info-compare.component';
import { TopNavTreeviewBtnComponent } from '../layouts/top-nav/top-nav-treeview-btn/top-nav-treeview-btn.component';
import { MtmCommentEditorComponent } from '../shared/components/mtm-comment-editor/mtm-comment-editor.component';
import { MtmInputDatepickerDirective } from '../shared/directives/mtm-input-datepicker.directive';
import { SlugifyDirective } from '../shared/directives/slugify.directive';
import { TrimDirective } from '../shared/directives/trim.directive';
import { FileDisplayNamePipe } from '../shared/pipes/file-display-name.pipe';
import { InitialNamePipe } from '../shared/pipes/initial-name.pipe';
import { KpfFolderNamePipe } from '../shared/pipes/kpf-folder-name.pipe';
import { MtmUserListDropdownComponent } from '../shared/components/top-media-info/mtm-user-list-dropdown/mtm-user-list-dropdown.component';
import { AssetListActionsComponent } from './asset-list-actions/asset-list-actions.component';
import { CheckIconComponent } from './svg/check-icon/check-icon.component';
import { ColumnViewIconComponent } from './svg/column-view-icon/column-view-icon.component';
import { ExportIconComponent } from './svg/export-icon/export-icon.component';
import { ListViewIconComponent } from './svg/list-view-icon/list-view-icon.component';
import { UncheckIconComponent } from './svg/uncheck-icon/uncheck-icon.component';
import { AutoCloseDropdownOnScrollDirective } from '../shared/directives/auto-close-dropdown-on-scroll.directive';

import { AutoGrowWidthDirective } from '../shared/directives/auto-grow-width.directive';
import { MasterIconComponent } from '../shared/components/master-icon/master-icon.component';
import { ZoomScaleDirective } from '../shared/directives/zoom-scale.directive';
import { UserProfilePictureComponent } from '../shared/components/mtm-profile-picture/mtm-user-profile-picture/mtm-user-profile-picture.component';
import { SafeHtmlPipe } from '../shared/pipes/safe-html.pipe';
import { SafeUrlPipe } from '../shared/pipes/safe-resource-url.pipe';
import { AgAlpineThemeDirective } from '../shared/directives/ag-alpine-theme.directive';
import { AssetVotersComponent } from '../project-subsection/asset-voters/asset-voters.component';
import { OldImageAnnotation } from '../shared/components/image-annotation/old-image-annotation.component';
import { OldAudioAnnotationComponent } from '../shared/components/audio-annotation/old-audio-annotation.component';
import { OldCommentAnnotation } from '../shared/components/old-comment-annotation/old-comment-annotation.component';
import { ApprovalYesIconComponent } from '../project-subsection/svg/approval-yes-icon/approval-yes-icon.component';
import { ApprovalNoIconComponent } from '../project-subsection/svg/approval-no-icon/approval-no-icon.component';
import { ApprovalNaIconComponent } from '../project-subsection/svg/approval-na-icon/approval-na-icon.component';
import { MtmCheckboxComponent } from '../shared/components/mtm-checkbox/mtm-checkbox.component';
import { FunnelHeaderComponent } from '../shared/components/asset-list/components/funnel-header/funnel-header.component';
import { AudioAnnotation } from '../shared/components/audio-annotation/audio-annotation.component';
import { MediaPlayerComponent } from '../shared/components/media-player/media-player.component';
import { ViewerRejectButtonComponent } from '../shared/components/viewer-controller/viewer-reject-button/viewer-reject-button.component';
import { ViewerApproveButtonComponent } from '../shared/components/viewer-controller/viewer-approve-button/viewer-approve-button.component';
import { ViewerCompareButtonComponent } from '../shared/components/viewer-controller/viewer-compare-button/viewer-compare-button.component';
import { ViewerDownloadButtonComponent } from '../shared/components/viewer-controller/viewer-download-button/viewer-download-button.component';
import { ViewerInfoButtonComponent } from '../shared/components/viewer-controller/viewer-info-button/viewer-info-button.component';
import { ViewerAnnotationToggleButtonComponent } from '../shared/components/viewer-controller/viewer-annotation-toggle-button/viewer-annotation-toggle-button.component';
import { ViewerSliderToggleButtonComponent } from '../shared/components/viewer-controller/viewer-slider-toggle-button/viewer-slider-toggle-button.component';
import { ViewerCinemaModeButtonComponent } from '../shared/components/viewer-controller/viewer-cinema-mode-button/viewer-cinema-mode-button.component';
import { ViewerFullscreenButtonComponent } from '../shared/components/viewer-controller/viewer-fullscreen-button/viewer-fullscreen-button.component';
import { ViewerSettingsButtonComponent } from '../shared/components/viewer-controller/viewer-settings-button/viewer-settings-button.component';
import { ViewerPlayButtonComponent } from '../shared/components/viewer-controller/viewer-play-button/viewer-play-button.component';
import { ViewerVolumeButtonComponent } from '../shared/components/viewer-controller/viewer-volume-button/viewer-volume-button.component';
import { ViewerTimeDisplayComponent } from '../shared/components/viewer-controller/viewer-time-display/viewer-time-display.component';
@NgModule({
  declarations: [
    // ImageCropperComponent,
    ProjectCompanyComponent,
    MTMTelInputComponent,
    LoadingDataComponent,
    MTMMainInfiniteScrol,
    UploadFileComponent,
    NewUploadFilesPreviewThumpnailComponent,
    MtmDropdownComponent,
    UserCardComponent,
    EmbedVideoComponent,
    CircleProgressComponent,
    MtmItemIconComponent,
    MtmResizeIndicatorIconComponent,
    MtmImageComponent,
    ButtonComponent,
    NotificationViewComponent,
    MTMTermConditionCheckComponent,
    ActivityLogComponent,
    MTMFileDownloadControl,
    MTMDownloadUploadProgressIcon,
    ProjectDescription,
    UploadModalComponent,
    UserAvatarDirective,
    GroupAvatarDirective,
    HasUserAccessDirective,
    CommentMinButtonsDirective,
    CommentTextAreaDirective,
    MTMDatePickerDirective,
    SSProfilePreviewImageDirective,
    PasswordToggleDirective,
    RightPaneContentDirective,
    ResizeObserverElementDirective,
    NumberDirective,
    PhoneNumberDirective,
    ShortenedUrlDirective,
    TrimDirective,
    SlugifyDirective,
    ClickOutsideDirective,
    KeysPipe,
    MtmDatePipe,
    MtmDatePipe2,
    MtmTimePipe,
    MtmDateTimePipe,
    MtmNgbDate,
    MtmFileSizePipe,
    MtmFileExtensionPipe,
    RoundedNumberPipe,
    CacheBusterImagePipe,
    projectFilterPipe,
    archivedProjectFilterPipe,
    OrdinalPipe,
    SafePipe,
    FilterPipe,
    CleanerPipe,
    ObjectToKeysPipe,
    FilterSearchUserPipe,
    FilterSearchProjectPipe,
    SimpleProjectFilterPipe,
    ProjectSortingPipe,
    FormatVersionNamePipe,
    ParseUrl,
    CleanUrl,
    SearchCompanyPipe,
    SearchUserPipe,
    SortCompanyPipe,
    PipeDropdownSearch,
    MTMTelInputPipe,
    ArraySortPipe,
    TranslatePipe,
    CommentDatePipe,
    InitialNamePipe,
    FileDisplayNamePipe,
    KpfFolderNamePipe,
    ToTimePipe,
    InViewDirective,
    FileNameDisplayDirective,
    ReviewUiLinkSettingsComponent,
    ReviewUiLinkPasswordComponent,
    ReviewUiLinkIdentityComponent,
    ReviewLinkAssetsPreviewComponent,
    MtmAssetsDropdownComponent,
    MtmInviterDropdownComponent,
    ReviewLinkNotFoundComponent,
    ReviewLinkRestrictedComponent,
    LoginFormComponent,
    Verify2faComponent,
    PreviewAllAssetComponent,
    RecentActivityComponent,
    CalendarWidgetComponent,
    HierarchicalFilesComponent,
    HRGridFile,
    HRListFile,
    HRPreviewFile,
    HRBadgebtn,
    MediaPlayerComponent,
    MtmPdfSliderComponent,
    TopMediaInfoComponent,
    TopMediaInfoCompareComponent,
    TopNavTreeviewBtnComponent,
    SubsectionPreviewAssetListComponent,
    AssetInfoComponent,
    VideojsAnnotationBarListComponent,
    ImageAnnotationToolbarComponent,
    ImageAnnotation,
    OldImageAnnotation,
    AudioAnnotation,
    OldAudioAnnotationComponent,
    VideoAnnotationComponent,
    PdfAnnotation,
    CommentAnnotation,
    OldCommentAnnotation,
    ImageDrawingTools,
    ViewerRejectButtonComponent,
    ViewerApproveButtonComponent,
    ViewerCompareButtonComponent,
    ViewerDownloadButtonComponent,
    ViewerInfoButtonComponent,
    ViewerAnnotationToggleButtonComponent,
    ViewerSliderToggleButtonComponent,
    ViewerCinemaModeButtonComponent,
    ViewerFullscreenButtonComponent,
    ViewerSettingsButtonComponent,
    ViewerPlayButtonComponent,
    ViewerVolumeButtonComponent,
    ViewerTimeDisplayComponent,
    CommentPopupComponent,
    CompanyRolesAndPermissionComponent,
    UploadFileModalComponent,
    ObserveThisDirective,
    ThrottleClickDirective,
    MtmInputDatepickerDirective,
    PreviewAssetComponent,
    PreviewAssetListComponent,
    TaskWidgetComponent,
    SingleTaskComponent,
    CreateTaskComponent,
    PreviewTaskComponent,
    TaskAssetPreviewComponent,
    TaskListComponent,
    TaskDropdownComponent,
    KeyFileSelectorComponent,
    MoveKeyFileComponent,
    MtmAssetControlComponent,
    MtmProjectAssetTreeComponent,
    MtmAssetTreeComponent,
    FilterDropdownComponent,
    FilterUser,
    ToUtcDate,
    DateCellRendererComponent,
    DateTimeCellRendererComponent,
    MtmTextEditorComponent,
    CommentPreviewComponent,
    TimelineSkeletonComponent,
    GridSkeletonComponent,
    ProjectsOwnershipTransferComponent,
    TeamEditorComponent,
    UrlShortenerComponent,
    ApprovalYesIconComponent,
    ApprovalNoIconComponent,
    ApprovalNaIconComponent,
    UrlShortenerEditorComponent,
    MtmAssetNameComponent,
    MtmAssetListComponent,
    AssetCardComponent,
    VariantStatusDropdownComponent,
    AssetStatusDropdownComponent,
    VariantDropdownMenuComponent,
    AssetDropdownMenuComponent,
    FunnelHeaderComponent,
    MtmEditAssetModalComponent,
    MtmAssetTypeSelectorComponent,
    MtmEditableLabel,
    InfoBannerComponent,
    PasswordCheckerComponent,
    PasswordStrengthComponent,
    GeneralLogComponent,
    MtmBreadcrumbComponent,
    MtmBreadcrumbProjectComponent,
    MtmBreadcrumbProjectCompareComponent,
    ProjectStagesComponent,
    RadialTransferBarComponent,
    PdfTranscodeErrorComponent,
    AssetScanInfoComponent,
    DisableShiftScrollDirective,
    MtmCommentEditorComponent,
    AssetsTableViewComponent,
    AssetListActionsComponent,
    ColumnViewIconComponent,
    ListViewIconComponent,
    CheckIconComponent,
    UncheckIconComponent,
    ExportIconComponent,
    AutoGrowWidthDirective,
    MasterIconComponent,
    ZoomScaleDirective,
    MtmUserListDropdownComponent,
    AutoCloseDropdownOnScrollDirective,
    UserProfilePictureComponent,
    SafeHtmlPipe,
    SafeUrlPipe,
    AgAlpineThemeDirective,
    AssetVotersComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  exports: [
    // ImageCropperComponent,
    ProjectCompanyComponent,
    MTMTelInputComponent,
    LoadingDataComponent,
    MTMMainInfiniteScrol,
    UploadFileComponent,
    NewUploadFilesPreviewThumpnailComponent,
    MtmDropdownComponent,
    UserCardComponent,
    EmbedVideoComponent,
    CircleProgressComponent,
    MtmItemIconComponent,
    MtmResizeIndicatorIconComponent,
    MtmImageComponent,
    ButtonComponent,
    NotificationViewComponent,
    MTMTermConditionCheckComponent,
    MTMFileDownloadControl,
    MTMDownloadUploadProgressIcon,
    ProjectDescription,
    UploadModalComponent,
    UserAvatarDirective,
    GroupAvatarDirective,
    HasUserAccessDirective,
    TrimDirective,
    CommentMinButtonsDirective,
    CommentTextAreaDirective,
    MTMDatePickerDirective,
    SSProfilePreviewImageDirective,
    AssetPreviewDirective,
    PasswordToggleDirective,
    RightPaneContentDirective,
    ResizeObserverElementDirective,
    NumberDirective,
    PhoneNumberDirective,
    ShortenedUrlDirective,
    KeysPipe,
    MtmDatePipe,
    MtmDatePipe2,
    MtmTimePipe,
    MtmDateTimePipe,
    MtmNgbDate,
    CacheBusterImagePipe,
    RoundedNumberPipe,
    projectFilterPipe,
    archivedProjectFilterPipe,
    OrdinalPipe,
    SafePipe,
    FilterPipe,
    CleanerPipe,
    ObjectToKeysPipe,
    FilterSearchUserPipe,
    FilterSearchProjectPipe,
    SimpleProjectFilterPipe,
    ProjectSortingPipe,
    ParseUrl,
    CleanUrl,
    SearchCompanyPipe,
    SearchUserPipe,
    SortCompanyPipe,
    PipeDropdownSearch,
    ArraySortPipe,
    TranslatePipe,
    CommentDatePipe,
    FormatVersionNamePipe,
    InitialNamePipe,
    FileDisplayNamePipe,
    KpfFolderNamePipe,
    ToTimePipe,
    InViewDirective,
    FileNameDisplayDirective,
    ReviewUiLinkSettingsComponent,
    ReviewUiLinkPasswordComponent,
    ReviewUiLinkIdentityComponent,
    ReviewLinkAssetsPreviewComponent,
    MtmAssetsDropdownComponent,
    MtmInviterDropdownComponent,
    ReviewLinkNotFoundComponent,
    ReviewLinkRestrictedComponent,
    LoginFormComponent,
    PreviewAllAssetComponent,
    HierarchicalFilesComponent,
    HRGridFile,
    HRListFile,
    HRPreviewFile,
    HRBadgebtn,
    MediaPlayerComponent,
    MtmPdfSliderComponent,
    TopMediaInfoComponent,
    TopMediaInfoCompareComponent,
    TopNavTreeviewBtnComponent,
    VideojsAnnotationBarListComponent,
    ImageAnnotation,
    OldImageAnnotation,
    PdfAnnotation,
    CommentAnnotation,
    OldCommentAnnotation,
    ImageAnnotationToolbarComponent,
    AudioAnnotation,
    OldAudioAnnotationComponent,
    VideoAnnotationComponent,
    ImageDrawingTools,
    ViewerRejectButtonComponent,
    ViewerApproveButtonComponent,
    ViewerCompareButtonComponent,
    ViewerDownloadButtonComponent,
    ViewerInfoButtonComponent,
    ViewerAnnotationToggleButtonComponent,
    ViewerCinemaModeButtonComponent,
    ViewerFullscreenButtonComponent,
    ViewerSettingsButtonComponent,
    ViewerPlayButtonComponent,
    ViewerVolumeButtonComponent,
    ViewerTimeDisplayComponent,
    CommentPopupComponent,
    ObserveThisDirective,
    MtmInputDatepickerDirective,
    ThrottleClickDirective,
    PreviewAssetComponent,
    PreviewAssetListComponent,
    RecentActivityComponent,
    CalendarWidgetComponent,
    TaskWidgetComponent,
    SingleTaskComponent,
    CreateTaskComponent,
    PreviewTaskComponent,
    TaskAssetPreviewComponent,
    TaskListComponent,
    TaskDropdownComponent,
    KeyFileSelectorComponent,
    MoveKeyFileComponent,
    MtmAssetControlComponent,
    MtmProjectAssetTreeComponent,
    MtmAssetTreeComponent,
    FilterDropdownComponent,
    FilterUser,
    ToUtcDate,
    DateCellRendererComponent,
    DateTimeCellRendererComponent,
    MtmTextEditorComponent,
    CommentPreviewComponent,
    TimelineSkeletonComponent,
    GridSkeletonComponent,
    ProjectsOwnershipTransferComponent,
    TeamEditorComponent,
    UrlShortenerComponent,
    ApprovalYesIconComponent,
    ApprovalNoIconComponent,
    ApprovalNaIconComponent,
    UrlShortenerEditorComponent,
    MtmAssetNameComponent,
    MtmAssetListComponent,
    AssetCardComponent,
    VariantStatusDropdownComponent,
    AssetStatusDropdownComponent,
    VariantDropdownMenuComponent,
    AssetDropdownMenuComponent,
    FunnelHeaderComponent,
    MtmEditAssetModalComponent,
    MtmAssetTypeSelectorComponent,
    MtmEditableLabel,
    InfoBannerComponent,
    PasswordCheckerComponent,
    PasswordStrengthComponent,
    MtmFileSizePipe,
    MtmFileExtensionPipe,
    GeneralLogComponent,
    MtmBreadcrumbComponent,
    MtmBreadcrumbProjectComponent,
    MtmBreadcrumbProjectCompareComponent,
    ProjectStagesComponent,
    RadialTransferBarComponent,
    PdfTranscodeErrorComponent,
    AssetScanInfoComponent,
    DisableShiftScrollDirective,
    ClickOutsideDirective,
    MtmCommentEditorComponent,
    SlugifyDirective,
    AssetsTableViewComponent,
    AssetListActionsComponent,
    ColumnViewIconComponent,
    ListViewIconComponent,
    CheckIconComponent,
    UncheckIconComponent,
    ExportIconComponent,
    MtmReadAllIconComponent,
    MtmNotificationSettingsIconComponent,
    MtmGlobeIconComponent,
    MtmReplyIconComponent,
    MtmUnmuteIconComponent,
    MtmArchiveIconComponent,
    MtmCheckedIconComponent,
    AutoGrowWidthDirective,
    MasterIconComponent,
    ZoomScaleDirective,
    MtmUserListDropdownComponent,
    AutoCloseDropdownOnScrollDirective,
    MtmChatSparkleIconComponent,
    MtmTaskIconComponent,
    UserProfilePictureComponent,
    SafeHtmlPipe,
    SafeUrlPipe,
    AgAlpineThemeDirective,
    AssetVotersComponent,
    MtmCheckboxComponent,
    LoadingSpinnerComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgClickOutsideDirective,
    DragDropModule,
    UiSwitchModule,
    NgbModule,
    ImageCropperModule,
    PickerModule,
    FullCalendarModule,
    NgSelectModule,
    QuillModule,
    NgxSkeletonLoaderModule,
    NgxSliderModule,
    NgOptimizedImage,
    MtmEditAssetTechnicalVariantsComponent,
    ScrollingModule,
    OverlayModule,
    MtmChatSparkleIconComponent,
    MtmTaskIconComponent,
    MtmReadAllIconComponent,
    MtmNotificationSettingsIconComponent,
    MtmGlobeIconComponent,
    MtmReplyIconComponent,
    MtmUnmuteIconComponent,
    MtmArchiveIconComponent,
    MtmCheckedIconComponent,
    MtmCheckboxComponent,
    // KanbanModule
    AssetPreviewDirective,
    LoadingSpinnerComponent,
    StatusSelectComponent,
    DriveProjectStagesComponent,
  ],
  providers: [FileDisplayNamePipe, provideHttpClient(withInterceptorsFromDi())],
})
export class SharedModule {}
